/**
 * Client store for the Stock screen.
 *
 * Holds the item list and the list's UI state (search text, page, page size,
 * filters). Rows are read from `GET stock/items/` by the page and hydrated here
 * by StockInitializer — the slice itself never fetches.
 *
 * **Search, both catalogue facets and the status filter are server-side.** What
 * lives here is the *intent*; `StockQuerySync` mirrors it into the URL, the
 * Server Component re-reads, and the slice is re-seeded with the matching page.
 * The two catalogue filters hold **uuids**, not names, because that is what the
 * `?category=` and `?storage_location=` params take.
 *
 * Per docs/frontend-conventions.md §9, feature-wide UI state belongs in Redux,
 * not component `useState`.
 */
import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

import type {
  ReceiveStockRow,
  StockItem,
  StockItemPage,
} from "@/components/stock/types";

/**
 * Which overlay is on screen. Only one can be open at a time, so this is a
 * single discriminator rather than a boolean per drawer. Grows as the section
 * is built — "details" and "edit" land with their own drawers.
 */
export type StockOverlay = "details" | "edit";

/**
 * Facets the Filter popover applies (Figma 2325:130405). Empty string means
 * "no filter", which keeps each facet a plain single-select — and an omitted
 * param, since sending an empty `?category=` is a 400 rather than "no filter".
 */
export interface StockFilters {
  /** Category **uuid**, not its name. */
  category: string;
  /** Storage-location **uuid**, not its name. */
  storageLocation: string;
  /** `out_of_stock` · `low` · `normal` · `healthy`. */
  status: string;
}

/**
 * The list state the URL carries — what `StockInitializer` adopts on mount.
 *
 * Mirrors the search params `StockQuerySync` writes, so the two halves of the
 * round trip cannot drift.
 */
export interface StockListQuery {
  search: string;
  filters: StockFilters;
  page: number;
  pageSize: number;
}

export const EMPTY_STOCK_FILTERS: StockFilters = {
  category: "",
  storageLocation: "",
  status: "",
};

export interface StockState {
  items: StockItem[];
  /** Total rows across all pages — drives "1-10 of 180". */
  total: number;
  page: number;
  pageSize: number;
  search: string;
  filters: StockFilters;
  /** Open overlay, or null when the list is uncovered. */
  overlay: StockOverlay | null;
  /** Item the open overlay acts on. */
  activeItemId: string | null;
  /**
   * The Receive Stock screen's editable lines. Feature state rather than local
   * `useState` per conventions §9 — several components on that screen read and
   * write it (the rows, the "N Items added" counter, the submit action).
   */
  receiveRows: ReceiveStockRow[];
  /** A list navigation is in flight — see `setStockListPending`. */
  listPending: boolean;
}

/**
 * A fresh, empty line — what "Add Another Item" appends.
 *
 * `unit` starts blank rather than at a default: it is not the operator's to
 * choose, it is whatever the item they pick is counted in, and seeding a guess
 * would show `pcs` beside an item sold by the kilo until they notice.
 */
export function blankReceiveRow(id: string): ReceiveStockRow {
  return {
    id,
    stockItemId: "",
    name: "",
    unit: "",
    quantity: 0,
    purchaseRate: 0,
    expiryDate: "",
  };
}

const initialState: StockState = {
  items: [],
  total: 0,
  page: 1,
  pageSize: 10,
  search: "",
  filters: EMPTY_STOCK_FILTERS,
  overlay: null,
  activeItemId: null,
  // The screen opens on one blank line, as the design shows
  receiveRows: [blankReceiveRow("row-1")],
  listPending: false,
};

const stockSlice = createSlice({
  name: "stock",
  initialState,
  reducers: {
    /**
     * True from the moment a query change starts navigating until the server
     * render lands. Set by `StockQuerySync`, which owns the transition; read by
     * the list, which is a sibling and cannot see it any other way.
     */
    setStockListPending(state, action: PayloadAction<boolean>) {
      state.listPending = action.payload;
    },
    /** Replace the list with a server-rendered page (called by StockInitializer). */
    setStockItems(state, action: PayloadAction<StockItemPage>) {
      state.items = action.payload.items;
      state.total = action.payload.total;
      state.page = action.payload.page;
      state.pageSize = action.payload.pageSize;
    },
    /**
     * Adopt the URL's list state verbatim, on mount.
     *
     * **Not the individual setters.** `setStockSearch` and `setStockFilters`
     * both reset `page` to 1 — correct when a user changes a facet, wrong here:
     * it would drop anyone landing on `?page=3&status=low` back to page one.
     *
     * Without this the URL is write-only. `StockQuerySync` rebuilds the query
     * from this slice and replaces the address bar, so a fresh store (a reload,
     * or a link someone shared) wipes the very params the page was rendered
     * with, roughly 400 ms after it painted them correctly.
     */
    setStockListQuery(state, action: PayloadAction<StockListQuery>) {
      state.search = action.payload.search;
      state.filters = action.payload.filters;
      state.page = action.payload.page;
      state.pageSize = action.payload.pageSize;
    },
    setStockPage(state, action: PayloadAction<number>) {
      state.page = action.payload;
    },
    setStockPageSize(state, action: PayloadAction<number>) {
      state.pageSize = action.payload;
      // A larger page size can put the current page out of range
      state.page = 1;
    },
    setStockSearch(state, action: PayloadAction<string>) {
      state.search = action.payload;
      // Any new query restarts paging from the first page
      state.page = 1;
    },
    setStockFilters(state, action: PayloadAction<StockFilters>) {
      state.filters = action.payload;
      state.page = 1;
    },
    clearStockFilters(state) {
      state.filters = EMPTY_STOCK_FILTERS;
      state.page = 1;
    },
    /** Open an overlay against a row. */
    openStockOverlay(
      state,
      action: PayloadAction<{ overlay: StockOverlay; itemId: string }>,
    ) {
      state.overlay = action.payload.overlay;
      state.activeItemId = action.payload.itemId;
    },
    closeStockOverlay(state) {
      state.overlay = null;
      state.activeItemId = null;
    },

    /** Append a blank line — "Add Another Item". */
    addReceiveRow(state, action: PayloadAction<{ id: string }>) {
      state.receiveRows.push(blankReceiveRow(action.payload.id));
    },
    /** Patch one field of one line as the user edits it. */
    updateReceiveRow(
      state,
      action: PayloadAction<{ id: string; patch: Partial<ReceiveStockRow> }>,
    ) {
      const row = state.receiveRows.find((r) => r.id === action.payload.id);
      if (row) Object.assign(row, action.payload.patch);
    },
    removeReceiveRow(state, action: PayloadAction<string>) {
      state.receiveRows = state.receiveRows.filter(
        (row) => row.id !== action.payload,
      );
      // Never leave the table empty — the design always shows an entry line
      if (state.receiveRows.length === 0) {
        state.receiveRows = [blankReceiveRow("row-1")];
      }
    },
    /** Discard the draft — "Cancel", and after a successful submit. */
    resetReceiveRows(state) {
      state.receiveRows = [blankReceiveRow("row-1")];
    },
  },
});

export const {
  setStockListPending,
  setStockItems,
  setStockListQuery,
  setStockPage,
  setStockPageSize,
  setStockSearch,
  setStockFilters,
  clearStockFilters,
  openStockOverlay,
  closeStockOverlay,
  addReceiveRow,
  updateReceiveRow,
  removeReceiveRow,
  resetReceiveRows,
} = stockSlice.actions;

export default stockSlice.reducer;
